import { useEffect } from "react";
import type { UseFormReturn, FieldValues, Path } from "react-hook-form";
import type { ApiValidationError } from "@schemas/common";
import { mapApiErrorToFieldPath } from "@schemas/common";

/**
 * Synchronizes API validation errors (from FastAPI) to react-hook-form field errors.
 *
 * @param form - The react-hook-form instance
 * @param apiErrors - Array of API validation errors received from the backend
 *
 * @example
 * const form = useForm<MyFormInput>();
 * useApiFormErrors(form, apiErrors);
 */
export function useApiFormErrors<T extends FieldValues>(
	form: UseFormReturn<T>,
	apiErrors: ApiValidationError[] | undefined,
): void {
	useEffect(() => {
		if (!apiErrors || apiErrors.length === 0) return;

		apiErrors.forEach((err) => {
			const fieldPath = mapApiErrorToFieldPath(err.loc);
			if (fieldPath) {
				form.setError(fieldPath as Path<T>, {
					type: "api",
					message: err.ctx?.error || err.msg,
				});
			}
		});
	}, [apiErrors, form]);
}
